/**
 * @file Lecture d'une erreur HTTP : la session est-elle finie, ou est-ce passager ?
 * @module packages.common.utils.authFailure
 *
 * C'est la distinction qui gouverne tout le comportement de session du client.
 * Une erreur réseau, un backend qui redémarre pendant un déploiement ou une
 * base momentanément lente ne ferment pas une session : seul un refus explicite
 * du serveur la ferme.
 *
 * Ce module ne fait que lire une erreur, il ne retente rien. La reprise
 * appartient à la couche qui possède la requête : SWR pour les données,
 * `SessionProvider` pour la session. L'intercepteur Axios n'en fait pas, sous
 * peine de multiplier les appels au moment où l'API est déjà fragile.
 *
 * Le serveur est seul juge de cette fermeture et le dit par `sessionClosed`. Le
 * statut HTTP ne suffirait pas : plusieurs contrôleurs métier répondent 401 pour
 * des raisons étrangères à l'authentification, et les traiter comme des fins de
 * session déconnecterait un utilisateur parfaitement authentifié.
 */

/**
 * Indique que le serveur a explicitement déclaré la session terminée.
 *
 * @param {Error} error - Erreur Axios
 * @return {boolean} True si le client doit fermer la session
 */
export const isSessionClosed = (error) => error?.response?.data?.sessionClosed === true;

/**
 * Indique un échec passager, qui justifie une nouvelle tentative.
 *
 * Une requête annulée volontairement (démontage de composant, navigation) n'en
 * est pas un : la retenter irait contre l'intention de l'appelant.
 *
 * @param {Error} error - Erreur Axios
 * @return {boolean} True si la requête mérite une nouvelle tentative
 */
export const isTransientFailure = (error) => {
  if (error?.code === 'ERR_CANCELED') {
    return false;
  }

  // Aucune réponse : coupure réseau, instance en cours de redémarrage,
  // passerelle qui n'a personne à joindre. Le serveur ne s'est pas prononcé.
  if (!error?.response) {
    return true;
  }

  const {status} = error.response;

  // 429 est délibérément exclu : le serveur dit « trop de demandes ». Retenter
  // consomme le quota et prolonge le blocage au lieu de le laisser retomber.
  // L'utilisateur doit être averti, pas servi en silence par des reprises.
  return status === 408 || status >= 500;
};
